import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil, tap } from 'rxjs/operators';

export interface AnchorMenuEntry {
  id: string;
  label: string;
  active: boolean;
  level: number;
  top: number;
  error: boolean;
}

export enum AnchorMenuMode {
  DEFAULT = 'default',
  SMALL = 'small'
}

@Component({
  selector: 'vfs-anchor-menu',
  templateUrl: './anchor-menu.component.html',
  styleUrls: ['./anchor-menu.component.scss']
})
export class AnchorMenuComponent implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private static entryDefaultHeight = 33;
  private static entrySmallHeight = 31;

  @Input() container: string;
  @Input() offset = 66;
  @Input() mode: AnchorMenuMode = AnchorMenuMode.DEFAULT;
  @Input() headers = 'h1, h2, h3, h4, h5, h6';
  @HostBinding() class = 'sticky';

  entries: AnchorMenuEntry[] = [];
  modes = AnchorMenuMode;
  url = this.getUrl();
  fragment = '';
  ballHeight = '20px';
  ballError = false;
  private scrollContainer: any;
  private destroyed = new Subject();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private element: ElementRef,
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_BASE_HREF) private baseHref: string
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroyed),
        filter(event => event instanceof NavigationEnd),
        tap(event => {
          const url = this.getUrl();
          if (this.url !== url) {
            this.entries = this.updateEntries();
            this.url = url;
          }
        })
      )
      .subscribe();

    this.route.fragment
      .pipe(
        takeUntil(this.destroyed),
        tap(fragment => {
          this.fragment = fragment;
          const target = document.getElementById(this.fragment);
          if (target) {
            target.scrollIntoView();
          }
        })
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.scrollContainer = this.container ? this.document.querySelectorAll(this.container)[0] : window;
    if (this.scrollContainer) {
      fromEvent(this.scrollContainer, 'scroll')
        .pipe(takeUntil(this.destroyed), debounceTime(10))
        .subscribe(() => this.onScroll());
      fromEvent(window, 'resize')
        .pipe(takeUntil(this.destroyed), debounceTime(100))
        .subscribe(() => {
          // TODO: Check horizontal redimension.
          this.onScroll();
        });
    }
    this.updateScrollPosition();
  }

  ngAfterViewChecked() {
    // FIXME: this is done on every cycle, so it can affect the performance of the display
    if (!location.href.includes('#') || this.entries.every(entry => entry.label === '')) {
      this.updateScrollPosition();
    }
  }

  ngOnDestroy() {
    this.destroyed.next();
  }

  updateScrollPosition(): void {
    this.entries = this.updateEntries();
    const target = document.getElementById(this.fragment);
    if (target && target.id !== 'undefined') {
      target.scrollIntoView();
    } else {
      this.onScroll();
    }
  }

  updateEntries(): AnchorMenuEntry[] {
    const entries: AnchorMenuEntry[] = [];
    const headers: HTMLElement[] = Array.from(this.document.querySelectorAll(this.headers));
    if (headers.length) {
      headers.forEach(header => {
        const name = header.innerText.trim();
        const { top } = header.getBoundingClientRect();
        entries.push({
          id: header.id,
          label: name,
          level: +header.tagName.substr(1),
          active: this.fragment === header.id,
          top,
          error: false
        });
      });
    }
    return entries;
  }

  protected onScroll(): void {
    let anyActive = false;
    for (let i = 0; i < this.entries.length; ++i) {
      this.entries[i].active = this.isLinkActive(anyActive, this.entries[i], this.entries[i + 1]);
      this.entries[i].error = this.hasError(this.entries[i]);
      if (!anyActive && this.entries[i].active) {
        anyActive = true;
        this.setBallSettings(this.entries[i], i);
      }
    }
  }

  protected setBallSettings(current: AnchorMenuEntry, position: number) {
    this.ballError = current.error;
    this.ballHeight =
      20 +
      position *
        (this.mode === this.modes.DEFAULT
          ? AnchorMenuComponent.entryDefaultHeight
          : AnchorMenuComponent.entrySmallHeight) +
      'px';
  }

  protected isLinkActive(anyActive: boolean, current: AnchorMenuEntry, next: AnchorMenuEntry | null): boolean {
    let result = false;
    const scrollOffset = this.getScrollOffset() + this.offset;
    const totallyScrolled =
      Math.round(this.scrollContainer.clientHeight) ===
      Math.round(this.scrollContainer.scrollHeight - this.scrollContainer.scrollTop);
    result = scrollOffset >= current.top && !(next && next.top < scrollOffset);
    if (totallyScrolled && next) {
      result = false;
    } else if (!anyActive && (scrollOffset < current.top || (!next && totallyScrolled))) {
      result = true;
    }
    return result;
  }

  protected hasError(current: AnchorMenuEntry): boolean {
    const target = document.getElementById(current.id);
    return target.classList.contains('vfs-error');
  }

  protected getScrollOffset(): number {
    const { top } = this.element.nativeElement.getBoundingClientRect();
    if (typeof this.scrollContainer.scrollTop !== 'undefined') {
      return this.scrollContainer.scrollTop + top;
    } else if (typeof this.scrollContainer.pageYOffset !== 'undefined') {
      return this.scrollContainer.pageYOffset + top;
    }
  }

  protected getUrl(): string {
    return this.baseHref + this.router.url.split('#')[0];
  }
}
